/* eslint-disable @typescript-eslint/ban-ts-ignore */
import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { SSO } from 'components/SSO/SSO';
import useAppLanguage from 'hooks/use-app-language';
import usePageConfig from 'hooks/use-page-config';
import { SSOAuthProviderKey } from 'entities/sso';
import { SSOMessages } from 'constants/constants';
import { registerWithProviderAction } from 'store/register/actions';
import { setRegisterWithSsoSuccess } from 'store/register/slice';
import LoginMethodModal from 'components/Auth/LoginMethodModal';
import _ from 'lodash';
import LoginMismatchModal from 'components/Auth/LoginMismatchModal';
import LoginWithSsoModal from 'components/Auth/LoginWithSsoModal';
import Button from '../../components/Button';
import { TextInput } from '../../components/Forms/TextInput';
import { useLoginUser } from '../../hooks/use-user-login';
import {
  FullClient,
  setSsoUserEmail,
  setSsoUserFirstName,
  setSsoUserLastName,
} from '../../store/auth/auth-slice';
import { RootState } from '../../store/reducer';
import messages from './messages';
import { LoginUserState } from './models/LoginUserState';
import { isValidEmail } from '../../utils/validation';
import cookie from '../../helpers/cookie';
import { loginWithProviderAction, logout } from '../../store/auth/auth-actions';

export default function LoginUser({
  onUserLoggedIn,
  onContinueTo2fa,
}: {
  onUserLoggedIn: () => void;
  onContinueTo2fa: (token: string) => void;
}) {
  // #region HOOKS
  const history = useHistory();
  const dispatch = useDispatch();
  const recaptchaRef = useRef(null);
  const [loginUser] = useLoginUser();
  const language = useAppLanguage();
  const pageConfig = usePageConfig();
  // #endregion

  // #region TRANSLATIONS
  const loginUserMessages = messages[language].loginUser;
  // #endregion

  // #region STATE
  const [state, setState] = useState<LoginUserState>({});
  const [ssoError, setSsoError] = useState<string>('');
  const [showLoginMethodModal, setShowLoginMethodModal] = useState<
    SSOAuthProviderKey | ''
  >('');
  const [showLoginWithSsoModal, setShowLoginWithSsoModal] = useState<
    SSOAuthProviderKey | ''
  >('');
  // #endregion

  // #region STORE
  const auth = useSelector((rootState: RootState) => rootState.auth);
  const registerWithSsoSuccess = useSelector(
    (rootState: RootState) => rootState.register.registerWithSsoSuccess,
  );
  const supportEmail = pageConfig?.App?.SupportEmail || 'support@altify.app';
  // #endregion

  // #region FUNCTIONS
  function isStateValid(value: LoginUserState): boolean {
    if (!value.username) return false;
    if (!value.password) return false;
    if (import.meta.env.VITE_ENV === 'production' && !value.recaptcha) {
      return false;
    }

    return isValidEmail(value.username);
  }

  function onLoginUserCallback(client: FullClient) {
    if (!client.success) {
      // @ts-ignore
      recaptchaRef.current.reset();
    }
    if (client.twoFactorRequired) {
      onContinueTo2fa(client.token);
    } else if (client.success) {
      onUserLoggedIn();
    } else {
      if (
        [
          SSOMessages.USER_SIGNED_UP_WITH_GOOGLE,
          SSOMessages.USER_SIGNED_UP_WITH_APPLE,
        ].includes(client?.message?.toUpperCase())
      ) {
        const provider: SSOAuthProviderKey = client.message?.slice(
          'USER_SIGNED_UP_WITH_'.length,
          client.message?.length,
        ) as SSOAuthProviderKey;

        setShowLoginWithSsoModal(provider);
      }
      setState({
        ...state,
        showUsernameError: true,
        showPasswordError: true,
        message: client?.message?.includes('Could not authenticate user')
          ? loginUserMessages.passwordErrorLabel
          : client.displayMessage || client.message,
        showLockMessage: client?.displayMessage?.includes(
          'your account has been locked',
        ),
      });
    }
  }

  async function onLoginUser(event: React.SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!isStateValid(state)) return;

    setState({ ...state, showUsernameError: false, showPasswordError: false });
    await loginUser({
      username: state.username || '',
      password: state.password || '',
      recaptcha: state.recaptcha || '',
      callback: onLoginUserCallback,
    });
  }
  // #endregion

  // #region LIFECYCLE

  useEffect(() => {
    if (auth.ssoLoading) {
      setSsoError('');
    }
  }, [auth.ssoLoading]);

  useEffect(() => {
    if (registerWithSsoSuccess) {
      history.push('/register/updateDetails');
      dispatch(setRegisterWithSsoSuccess(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerWithSsoSuccess]);
  // #endregion

  return (
    <div className="flex flex-col bg-ghost-white">
      <form onSubmit={onLoginUser}>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="font-semibold leading-9 mt-6 text-2.5x1 text-black text-center tracking-wide">
            {loginUserMessages.heading('Altify')}
          </h2>
          <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-wide mx-6 sm:mx-0">
            {loginUserMessages.subHeading}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="m-6">
            <TextInput
              autofocus
              name="email"
              type="email"
              label={loginUserMessages.usernameLabel}
              warning={state.showUsernameError}
              onChange={value => {
                setState({ ...state, username: value as string });
              }}
            />
            <TextInput
              name="password"
              type="password"
              label={loginUserMessages.passwordLabel}
              warning={state.showPasswordError}
              warningMessage={
                <div className="text-error text-sm">{state.message}</div>
              }
              onChange={value => {
                setState({ ...state, password: value as string });
              }}
            />

            <div className="mt-6 flex items-center justify-between flex-col">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY as string}
                onChange={value => {
                  setState({ ...state, recaptcha: (value as string) || '' });
                }}
              />
            </div>

            {!state.showLockMessage && state.showPasswordError && (
              <div className="mt-6">
                <p className="text-gray-900 text-center">
                  {loginUserMessages.supportErrorLabelBeforeEmail}&nbsp;
                  <a href={`mailto:${supportEmail}`} className="underline">
                    {supportEmail}
                  </a>
                  &nbsp;
                  {loginUserMessages.supportErrorLabelAfterEmail}
                </p>
              </div>
            )}

            {state.showLockMessage && (
              <div className="mt-6">
                <p className="text-gray-900 text-center">
                  {loginUserMessages.supportLockLabelBeforeEmail}&nbsp;
                  <a href={`mailto:${supportEmail}`} className="underline">
                    {supportEmail}
                  </a>
                  &nbsp;
                  {loginUserMessages.supportLockLabelAfterEmail}
                </p>
              </div>
            )}

            <div className="text-center w-full mt-4">
              <span className="text-primary-button ">
                {loginUserMessages.forgotPasswordLink}
              </span>
              &nbsp;
              <Link
                to="/reset-password"
                className="font-normal text-primary-button underline hover:no-underline"
              >
                {loginUserMessages.resetPasswordHere}
              </Link>
            </div>

            <div className="mt-6">
              <span className="block w-full rounded-md">
                <Button
                  main
                  full
                  id="button:login:continue"
                  onClick={onLoginUser}
                  disabled={
                    !isStateValid(state) ||
                    (auth.ssoLoading as string)?.length > 0
                  }
                  loading={
                    auth?.client?.loading &&
                    !state.showPasswordError &&
                    !state.showUsernameError
                  }
                  type="submit"
                >
                  {loginUserMessages.continueButton}
                </Button>
              </span>
            </div>

            <div className="flex flex-row align-middle justify-items-center items-center my-2">
              <hr className="flex-grow" />
              <div className="mx-2">OR</div>
              <hr className="flex-grow" />
            </div>

            <SSO
              flow="login"
              loadingProvider={auth.ssoLoading}
              error={ssoError}
              onSuccess={(key: SSOAuthProviderKey, response) => {
                const userData = {
                  email: response.user?.email,
                  firstName: response?.user?.displayName?.split(' ')?.[0],
                  lastName: response?.user?.displayName?.split(' ')?.[1],
                };

                // TODO: compress to a single dispatch(setSsoUserDetails(userData))
                dispatch(setSsoUserFirstName(userData?.firstName));
                dispatch(setSsoUserLastName(userData?.lastName));
                dispatch(setSsoUserEmail(userData?.email));

                dispatch(
                  loginWithProviderAction(
                    {
                      provider: key,
                      clientApplicationSsoCode: `DASHBOARD_${key}`,
                      ssoJwt: response?.user?.accessToken,
                    },
                    (res: any) => {
                      if (res.success) {
                        if (res.twoFactorRequired) {
                          onContinueTo2fa(res.token);
                        } else {
                          onUserLoggedIn();
                        }
                      } else if (
                        res.message === SSOMessages.USER_DOES_NOT_EXIST
                      ) {
                        dispatch(
                          registerWithProviderAction({
                            provider: key,
                            clientApplicationSsoCode: `DASHBOARD_${key}`,
                            ssoJwt: response?.user?.accessToken,
                          }),
                        );
                      } else if (
                        res.message === SSOMessages.USER_SIGNED_UP_WITH_PASSWORD
                      ) {
                        setSsoError(loginUserMessages.userSignedUpWithPassword);
                        setShowLoginMethodModal(key);
                      } else if (
                        [
                          SSOMessages.USER_SIGNED_UP_WITH_GOOGLE,
                          SSOMessages.USER_SIGNED_UP_WITH_APPLE,
                        ].includes(res?.message?.toUpperCase())
                      ) {
                        const provider: SSOAuthProviderKey = res.message?.slice(
                          'USER_SIGNED_UP_WITH_'.length,
                          res.message?.length,
                        ) as SSOAuthProviderKey;

                        setShowLoginWithSsoModal(provider);
                      }
                    },
                  ),
                );
              }}
              onError={error => {
                console.log({ error });
                setSsoError(loginUserMessages.ssoError);
              }}
            />

            <div className="mt-6 text-center w-full">
              <span className="text-center w-full">
                <span className="text-primary-button">
                  {loginUserMessages.needAccount}
                </span>
                &nbsp;{' '}
                <Link
                  to="/register"
                  className="font-normal text-right text-primary-button underline hover:no-underline"
                >
                  {loginUserMessages.registerLink}
                </Link>
              </span>
            </div>

            <LoginMismatchModal
              signinMethod={showLoginMethodModal as SSOAuthProviderKey}
              show={!_.isEmpty(showLoginMethodModal)}
              onClose={() => setShowLoginMethodModal('')}
            />

            <LoginWithSsoModal
              signinMethod={showLoginWithSsoModal as SSOAuthProviderKey}
              show={!_.isEmpty(showLoginWithSsoModal)}
              onClose={() => setShowLoginWithSsoModal('')}
              onUserLoggedIn={onUserLoggedIn}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
